import { DitherLoading } from "@/components/ui/dither-loading";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef, useState, type MouseEvent, type RefObject } from "react";
import { UNSAFE_NavigationContext, useLocation, useNavigate } from "react-router";
import { leaveOnce } from "@/components/assignments/assignment-draft-pop-interceptor";
import { useAuth } from "@/contexts/AuthContext";
import { Bento, PageRefusal } from "@/components/ui/box";
import type { Box, ListBox, Read, RowAct } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api/client";
import { courseDeskApi, type CourseDesk as Desk, type DeskPerspective, type DeskRow, type DeskSource, type DeskState } from "@/lib/api/courseDesk";
import { courseHomeQueryKey } from "@/hooks/use-course-home";
import { formatCourseInstant, homeOffer } from "@/lib/seat-workspace";
import { CourseHeaderDestinations, CourseViewNavigation } from "./CourseViewNavigation";
import { CourseBand } from "./course-frame";
import { courseBandMeta } from "./course-band-meta";
import { useCourseSectionTag } from "@/hooks/use-course-section-tag";
import { focusFrameHeading, useVisitOpen } from "./use-frame-focus";
import type { CourseWorkspaceShell } from "@/navigation/routes";
import { DESK_ORIGIN, DeskMemoryContext, deskOwner, readDesk, withDeskReading, type DeskMemory, type DeskReading } from "./desk/desk-memory";
import { TheCourse } from "./desk/TheCourse";
import { CourseHelp } from "@/components/help/CourseHelp";

const DESK_COPY = {
  refused: "You can't open this course.",
  refusedBack: "Back to your courses",
  allNotices: "All notices",
  recentUnread: "Recent updates could not be read. All notices reads them on its own.",
} as const;

/**
 * As the person leaves the Desk by a link, its reading goes into the Desk's
 * history entry, and a session of this course is pushed marked as opened
 * from it, so Return steps Back to the Desk as it was left (contract 4).
 * Recording and going are one departure: unsaved work is asked about once,
 * and a refusal does neither. Any other link then follows its own navigation.
 */
function useDeskLeave(courseId: string, owner: string, memory: DeskMemory) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  return (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank" || anchor.closest("[data-visit]")) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname === location.pathname && url.search === location.search) return;
    const row = anchor.closest<HTMLElement>("[data-row]");
    const box = anchor.closest<HTMLElement>("[data-box-id]");
    const reading: DeskReading = {
      ...memory.live,
      owner,
      scrollY: window.scrollY,
      opener: row && box?.dataset.boxId ? { box: box.dataset.boxId, row: row.dataset.row ?? "", href: anchor.getAttribute("href") ?? undefined, detail: Boolean(anchor.closest("[data-row-open]")) } : null,
    };
    const session = url.searchParams.get("session");
    const ownSession = url.pathname === location.pathname && session && [...url.searchParams.keys()].every(key => key === "session" || key === "view");
    event.preventDefault();
    leaveOnce(navigator, () => {
      // Replacing the Desk entry carries the reading, beside any visit memory, into history for Back and Return.
      navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: withDeskReading(location.state, reading) });
      if (ownSession) { navigate(`${url.pathname}${url.search}`, { state: { [DESK_ORIGIN]: courseId } }); return; }
      // Keyboard activation can already be inside the anchor's click steps;
      // recursively calling click() is then ignored. Follow its address once,
      // inside the same approved departure, for keyboard and pointer alike.
      navigate(`${url.pathname}${url.search}${url.hash}`);
    });
  };
}

/** A returned-to Desk focuses the row the person left from (never selects it) and its scroll, once drawn. */
function useDeskReturn(memory: DeskMemory, ready: boolean) {
  const restored = memory.restored;
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current || !restored.opener) return;
    const opener = restored.opener;
    const started = Date.now();
    let timer = 0;
    const attempt = () => {
      const row = [...document.querySelectorAll<HTMLElement>(`[data-box-id="${CSS.escape(opener.box)}"] [data-row="${CSS.escape(opener.row)}"]`)]
        .find(candidate => !candidate.closest("[hidden],[inert]"));
      const target = row && (opener.href ? [...row.querySelectorAll<HTMLAnchorElement>("a[href]")].find(link => link.getAttribute("href") === opener.href && Boolean(link.closest("[data-row-open]")) === Boolean(opener.detail)) : null)
        || row?.querySelector<HTMLElement>("[data-act]") || row?.querySelector<HTMLElement>('[tabindex="-1"]');
      if (target) {
        done.current = true;
        memory.returned = true;
        target.focus({ preventScroll: true });
        if (restored.scrollY !== undefined) window.scrollTo(0, restored.scrollY);
        return;
      }
      if (Date.now() - started < 8000) timer = window.setTimeout(attempt, 100);
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [ready, restored, memory]);
}

const WORDS: Record<DeskState, string> = {
  quiz_in_progress: "Quiz in progress", replacement_ready: "Replacement ready to start", replacement_submitted: "Replacement submitted", practice_available: "Optional practice", practice_checked: "Practice checked", optional_quiz: "Optional quiz",
  guide_needed: "Guide to prepare", no_topics: "No topics yet", topic_dates_and_purpose: "Topic dates and purpose to prepare", undated_topic: "No topic dates", topic_purpose_needed: "Purpose to prepare", private_changes: "Private changes to publish",
  reading_reference_needed: "Reference to prepare", reading_date_and_publication: "Reading date and publication to prepare", undated_reading: "No reading date", current_topic: "Current topic", upcoming_topic: "Next topic", reading_to_prepare: "To read", regrade_requested: "Regrade requested",
  in_progress: "In progress", held: "Held", ended: "Ended", scheduled: "Scheduled", published: "Published",
  record_outcomes: "Outcomes to record", close_session: "Close this session", publish_guidance: "Unpublished guidance", review: "Ready for review", release: "Ready to release",
  awaiting_release: "Awaiting release", awaiting_handins: "Awaiting hand-ins", awaiting_completion: "Awaiting completion", draft: "Private draft", revise: "Returned for revision", hand_in: "Ready to hand in",
  awaiting_feedback: "Awaiting feedback", feedback_released: "Feedback released", in_class: "In class", not_open: "Not open yet", deadline_passed: "Deadline passed", work_closed: "Work closed", term_closed: "Term closed", course_draft: "Course not published",
  missing_structure: "Details needed", recorded: "Recorded", unassigned: "Primary instructor needed", assigned: "Assigned", empty_roster: "No learners enrolled", enrolled: "Enrolled",
  publication_blocked: "Publication needs attention", ready_to_publish: "Can publish", awaiting_preparation: "Awaiting published teaching",
};
const SOURCE_NAMES: Record<DeskSource, string> = {
  preparation: "Course preparation", content: "Published course content", primary: "Teaching handoff", record: "Course record", session: "Sessions", coursework: "Coursework", follow_up: "Published session follow-up", recap: "Teaching recap status", drafts: "Private teaching drafts", notices: "Course notices", regrades: "Regrade requests",
};
const LABELS: Partial<Record<DeskRow["kind"], string>> = { course_details: "Course details", teaching_team: "Teaching team", roster: "Course roster", course_publication: "Course publication", teaching_preparation: "Teaching preparation" };
const VERBS: Partial<Record<DeskState, string>> = { deadline_passed: "Read deadline and next step", guide_needed: "Prepare guide", quiz_in_progress: "Continue quiz", replacement_ready: "Open quiz", practice_checked: "Read results", practice_available: "Read practice rules", optional_quiz: "Read quiz rules", no_topics: "Add topic", current_topic: "Open topic", upcoming_topic: "Open topic", reading_to_prepare: "Open reading", private_changes: "Review", record_outcomes: "Record outcomes", review: "Review", release: "Review and release", draft: "Continue", revise: "Revise", hand_in: "Open response", close_session: "Finish", publish_guidance: "Review guidance", ready_to_publish: "Review and publish", missing_structure: "Complete details", unassigned: "Assign teacher", regrade_requested: "Decide regrade" };
function title(row: DeskRow): string {
  if (LABELS[row.kind]) return LABELS[row.kind]!;
  const session = row.place?.session_no;
  if (["session", "follow_up", "guidance"].includes(row.kind)) return [session !== null && session !== undefined ? `Session ${session}` : "Session", row.title].filter(Boolean).join(" · ");
  return row.title || "Untitled teaching draft";
}
/** How each recipient's attempt arrived, in the learner act's own words. */
const ARRIVED: Record<NonNullable<NonNullable<DeskRow["coverage"]>["learner_act"]> | "recorded", string> = { hand_in: "handed in", mark_done: "marked done", mark_taken: "marked as taken", recorded: "recorded" };
function facts(row: DeskRow): string {
  const place = row.kind === "coursework" ? [row.place?.unit_title, row.place?.session_no !== null && row.place?.session_no !== undefined ? `Session ${row.place.session_no}` : null].filter(Boolean).join(" · ") : "";
  const own = row.coverage;
  const coverage = own ? `${own.learner_act ? own.submitted_count : own.recorded_count} of ${own.enrolled_count} recipients ${ARRIVED[own.learner_act ?? "recorded"]} · ${own.awaiting_review_count} to review · ${own.awaiting_release_count} awaiting release` : "";
  const handoff = row.handoff ? `Next: ${row.handoff.name ?? (row.handoff.kind === "primary" ? "Primary instructor" : row.handoff.kind === "registrar" ? "Administration" : "Learners")}` : "";
  const reading = row.kind === "reading" && row.place?.session_no != null ? `Before Session ${row.place.session_no}` : "";
  return [place, reading, replacement(row), coverage, handoff].filter(Boolean).join(" · ");
}
function replacement(row: DeskRow): string {
  const own = row.replacement;
  if (!own) return "";
  if (row.state === "replacement_ready") return `For attempt ${own.replaces}`;
  // Only a released, readable result of the replaced attempt can count meanwhile.
  const counts = own.score !== null && own.points !== null ? `attempt ${own.replaces}'s ${own.score} of ${own.points} counts until then` : null;
  return [own.ordinal !== null ? `Attempt ${own.ordinal}` : null, "result not released", counts].filter(Boolean).join(" · ");
}
function action(row: DeskRow): RowAct {
  const verb = VERBS[row.state] ?? "Open";
  return row.destination.kind === "visit" ? { does: "visit", verb, visit: row.destination.target } : { does: "exit", verb, to: row.destination.target };
}

/** The Desk's band is every view's band: one course frame (#1416). */
function DeskBand({ bandRef, courseId, course, courseHref, teaching }: { bandRef: RefObject<HTMLDivElement | null>; courseId: string; course: Desk["course"]; courseHref: string; teaching: boolean }) {
  const sectionTag = useCourseSectionTag(courseId, teaching);
  return <CourseBand bandRef={bandRef} code={course.code} eyebrow={course.term?.name} title={course.title} meta={courseBandMeta(course, sectionTag)} action={<CourseHeaderDestinations courseHref={courseHref} teaching={teaching} />} />;
}

/** The existing course invalidation prefix includes this independent projection. */
export function CourseDesk({ courseId, perspective, backHref, courseHref, canEdit = false, shell }: { courseId: string; perspective: DeskPerspective; backHref: string; courseHref?: string; canEdit?: boolean; shell?: CourseWorkspaceShell }) {
  const { me } = useAuth();
  const location = useLocation();
  // One Desk is one course seen from one perspective (its parent keys it so);
  // a reading another Desk wrote is ignored.
  const owner = deskOwner(courseId, perspective);
  const [memory] = useState<DeskMemory>(() => {
    const restored = readDesk(location.state, owner);
    return { restored, live: { ...restored, opener: null }, returned: false };
  });
  const leave = useDeskLeave(courseId, owner, memory);
  const queryClient = useQueryClient();
  const tenantId = me?.tenant_id, userId = me?.user_id;
  const queryKey = useMemo(() => [...courseHomeQueryKey(tenantId, userId, perspective === "administration" ? "teaching" : perspective, courseId), "desk", perspective], [tenantId, userId, perspective, courseId]);
  // The Desk endpoint's non-disclosing 404 refuses this projection only: its
  // cached rows go and the refusal is held until the identity changes. It is
  // not proof the login has lost the course (the learner read requires a
  // published course, the teaching read an active assignment), so every other
  // read keeps its own authority (#1393 R6, contract 4). A 401 or 403 refuses
  // the Desk and keeps its last rows out of view.
  const identity = queryKey.join(":");
  const [lostIdentity, setLostIdentity] = useState<string | null>(null);
  const lost = lostIdentity === identity;
  const query = useQuery({
    queryKey,
    enabled: Boolean(tenantId && userId && courseId) && !lost,
    queryFn: async () => {
      try {
        const desk = await courseDeskApi.get(courseId, perspective);
        setLostIdentity(previous => previous === identity ? null : previous);
        return desk;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) setLostIdentity(identity);
        throw error;
      }
    },
    retry: false,
  });
  useEffect(() => {
    if (lost) queryClient.removeQueries({ queryKey, exact: true });
  }, [lost, queryClient, queryKey]);
  const refused = lost || query.error instanceof ApiError && [401, 403, 404].includes(query.error.status);
  // Arrival focuses the frame's heading once it is the course's, so Return to
  // course and the Desk tab land somewhere. An open visit owns focus (R6).
  const band = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const arrived = useRef(false);
  const visitOpen = useVisitOpen();
  const ready = Boolean(courseHref && query.data);
  const returning = Boolean(memory.restored.opener);
  useEffect(() => {
    if (!ready || arrived.current) return;
    arrived.current = true;
    // A Desk returned to focuses the row the person left from instead. An
    // addressed target that already holds focus keeps it (the Resources part);
    // one that settles later (Find, a Calendar order row) moves it on from the
    // heading, and one that never draws leaves it there (#1393 M4-A6).
    if (!returning && !root.current?.contains(document.activeElement)) focusFrameHeading(band.current?.querySelector<HTMLElement>("h1") ?? null, visitOpen);
  }, [ready, visitOpen, returning]);
  useDeskReturn(memory, ready);
  // One course refusal (R8): Desk says what every view says.
  // Help a person keeps after the course ends or is archived stays reachable under the refusal, and nothing else does (#1294 H-1).
  if (refused) return <>
    <PageRefusal id={`desk-${courseId}`} journey="course-desk" refusal={{ reason: "forbidden", sentence: DESK_COPY.refused, back: { label: DESK_COPY.refusedBack, to: backHref } }} />
    {courseHref && <CourseHelp courseId={courseId} teaching={perspective !== "learning"} timezone={null} retainedOnly />}
  </>;
  const teaching = perspective !== "learning";
  return <DeskMemoryContext.Provider value={memory}><div ref={root} onClickCapture={leave} className="contents">
    {courseHref && <>
      {query.data ? <DeskBand bandRef={band} courseId={courseId} course={query.data.course} courseHref={courseHref} teaching={perspective === "teaching"} /> : <h1 className="text-title font-semibold">Course Desk</h1>}
      <CourseViewNavigation courseHref={courseHref} selectedSessionId={null} teaching={perspective === "teaching"} activeView="desk" />
    </>}
    <CourseDeskRead courseId={courseId} projection={query.data} pending={query.isPending} stale={query.isRefetchError} retry={() => void query.refetch()} courseHref={courseHref} />
    {/* Help follows Your work (#1294, additive). */}
    {courseHref && query.data && <CourseHelp courseId={courseId} teaching={teaching} timezone={query.data.course.timezone} />}
    {courseHref && query.data && <TheCourse courseId={courseId} courseHref={courseHref} teaching={teaching} canEdit={canEdit} termOpen={query.data.course.term?.status !== "closed"}
      timezone={query.data.course.timezone} shell={shell ?? (teaching ? "instructor" : "student")} />}
  </div></DeskMemoryContext.Provider>;
}

export function CourseDeskRead({ courseId, projection, pending = false, stale = false, retry, courseHref }: { courseId: string; projection?: Desk; pending?: boolean; stale?: boolean; retry(): void; courseHref?: string }) {
  const failed = Object.entries(projection?.sources ?? {}).filter(([, state]) => state === "unavailable").map(([source]) => source as DeskSource);
  const complete = failed.length === 0;
  const read = (rows: DeskRow[], empty: string, unread = "This course Desk could not be read."): Read<DeskRow[]> => !projection ? pending ? { status: "loading" } : { status: "failed", sentence: unread, retry }
    : stale ? { status: "stale", last: rows, readAt: projection.generated_at, sentence: "The Desk could not refresh. These are the last confirmed rows.", refresh: retry }
      : rows.length ? { status: "ready", value: rows, readAt: projection.generated_at }
        : { status: "empty", sentence: complete ? empty : "Some course work could not be read. Retry the reads above." };
  const box = (bucket: "now" | "waiting" | "recent" | "later", label: string, empty: string): ListBox<DeskRow[], DeskRow> => {
    const id = `desk-${bucket}-${courseId}`;
    // Recent updates is compact: its three newest notices, then All notices
    // (R3). Its other updates have no such page, so each stays.
    const all = projection?.[bucket] ?? [];
    const rows = bucket === "recent" ? all.filter((row, index) => row.kind !== "notice" || all.slice(0, index).filter(other => other.kind === "notice").length < 3) : all;
    // Later rests folded and Recent updates is a disclosure, so neither buries
    // The course (contract 5, R3). An empty or unread bucket stays drawn: its
    // sentence, and Recent's All notices, are always reachable.
    const discloses = (bucket === "later" || bucket === "recent") && rows.length > 0 && Boolean(projection) && !stale;
    return {
      ...(bucket === "recent" && courseHref ? { exits: [{ label: DESK_COPY.allNotices, to: `${courseHref}?view=notices` }] as [{ label: string; to: string }] } : {}),
      id, kind: "list", ...(complete && !discloses ? { home: true } : {}), label, job: "Read this seat's authorized course work and visit its existing owner.", owner: { system: "imam" },
      fold: discloses ? "choice" : "never",
      ...(discloses ? { act: { does: "open" as const, verb: bucket === "later" ? "Show later work" : "Show recent updates" } } : {}),
      detail: rows => discloses && rows.length ? rows.length === 1 ? title(rows[0]) : `${title(rows[0])} and ${rows.length - 1} more` : "",
      read: read(rows, empty, bucket === "recent" ? DESK_COPY.recentUnread : undefined), state: rows => ({ class: "quiet", word: complete ? String(rows.length) : "Partially read" }), rows: rows => rows,
      ...(bucket === "now" && projection ? homeOffer({ ranking: projection.ranking, offers: projection.offer ? { [id]: projection.offer } : {} }, id, new Set(rows.map(row => row.id)), stale) : {}),
      row: {
        key: row => row.id, title,
        nav: row => row.destination.kind === "navigate" ? row.destination.target : undefined,
        state: row => ({ class: row.handoff ? "waiting" : ["published", "held", "recorded", "assigned", "enrolled", "feedback_released"].includes(row.state) ? "done" : "quiet", word: WORDS[row.state] }),
        facts: row => [facts(row), row.at && projection ? formatCourseInstant(row.at, projection.course.timezone) : null].filter(Boolean).join(" · "),
        act: action,
        startsOpen: row => Boolean(row.guidance),
        detail: row => row.guidance ? <>
          {row.guidance?.recap && <p className="whitespace-pre-wrap">{row.guidance.recap}</p>}
          {row.guidance?.next_steps && <p className="whitespace-pre-wrap">{row.guidance.next_steps}</p>}
        </> : undefined,
      },
    };
  };
  // Your work in one order (contract 5): Now, Waiting on others, Later, then
  // Recent updates, the least actionable, always drawn so All notices is: a
  // Desk still loading or transiently unread keeps that exit, since notices
  // have their own read (R3). A refusal never reaches here (PageRefusal).
  const boxes: Box[] = [box("now", "Now", "Nothing needs you right now.")];
  if (projection?.waiting.length) boxes.push(box("waiting", "Waiting on others", "No handoffs are waiting."));
  if (projection?.later.length) boxes.push(box("later", "Later", "No later work."));
  boxes.push(box("recent", "Recent updates", "No recent updates."));
  return <>
    {failed.length > 0 && <div role="status" className="space-y-2 py-3 text-ui">{failed.map(source => <p key={source}>{SOURCE_NAMES[source]} could not be read. <Button variant="subtle" size="sm" onClick={retry}>Retry {SOURCE_NAMES[source].toLowerCase()}</Button></p>)}</div>}
    <DitherLoading><Bento id={`course-desk-${courseId}`} journey="course-desk" boxes={boxes} defaultOpen={[`desk-recent-${courseId}`]} /></DitherLoading>
  </>;
}
